import {
  useErrorHandlingMutation,
  useErrorHandlingQuery,
} from '@backoffice-frontend/data-fetching'
import { useMoiaSuccessNotification } from '@backoffice-frontend/patterns'
import type { useBackofficeLoggedInUser } from '@backoffice-frontend/user-management-ui'
import { useRefreshPermissions } from '@backoffice-frontend/user-management-ui'
import {
  ActionItemGroup,
  ActionItemMultiSelect,
  Button,
  Callout,
  LoadingIndicator,
  SelectionMenu,
  type useSelectionMenu,
} from '@moia-dev/pace-web'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useAccountBadgeAdminPanelGroupSwitcherQuery,
  useSwitchAdminPanelGroupMutation,
} from './AccountBadgeAdminPanelGroupSwitcher.hook'

type AccountBadgeAdminPanelGroupSwitcherProps = {
  selectionMenuProps: ReturnType<typeof useSelectionMenu>
  user: NonNullable<ReturnType<typeof useBackofficeLoggedInUser>['user']>
}

export const AccountBadgeAdminPanelGroupSwitcher = ({
  selectionMenuProps,
  user,
}: AccountBadgeAdminPanelGroupSwitcherProps) => {
  const { t } = useTranslation()
  const refreshPermissions = useRefreshPermissions()
  const [query, setQuery] = useState('')
  const userGroups =
    user.adminPanelTimedGroups?.map(group => group.groupName) ?? []
  const [selected, setSelected] = useState<string[]>(userGroups)
  const [switchGroup] = useErrorHandlingMutation(
    useSwitchAdminPanelGroupMutation,
  )
  const { data, loading } = useErrorHandlingQuery(
    useAccountBadgeAdminPanelGroupSwitcherQuery,
    { variables: {} },
  )
  const { enqueueMoiaSuccessNotification } = useMoiaSuccessNotification()

  const handleSubmit = async () => {
    if (selected.length === 0) return
    await switchGroup({
      variables: { input: { groupsList: selected, expires: null } },
    })
    enqueueMoiaSuccessNotification(
      t('authenticatedAppView_adminPanelGroupSwitchedSuccess'),
    )
    await refreshPermissions({ skipSuccessToast: true })
  }

  const filteredGroups = (data?.groups ?? [])
    .filter(group => group.assignedOrganizations?.includes(user.organizationId))
    .filter(option =>
      query.length > 0
        ? option.displayName.toLowerCase().includes(query.toLowerCase()) ||
          option.name.toLowerCase().includes(query.toLowerCase())
        : true,
    )

  const currentlyAssignedGroups = filteredGroups.filter(option =>
    userGroups.includes(option.name),
  )
  const otherGroups = filteredGroups.filter(
    option => !userGroups.includes(option.name),
  )

  const toggleGroup = (groupName: string) => {
    setSelected(
      selected.includes(groupName)
        ? selected.filter(item => item !== groupName)
        : [...selected, groupName],
    )
  }

  return (
    <SelectionMenu
      {...selectionMenuProps}
      title={t('authenticatedAppView_adminPanelGroupSwitcher_header')}
      caption={t('authenticatedAppView_adminPanelGroupSwitcher_caption')}
      footer={
        <div
          css={theme => ({
            display: 'flex',
            flexDirection: 'column',
            gap: theme.space.Space2,
          })}
        >
          <div css={{ width: 340 }}>
            <Callout hideDismiss>
              {t('authenticatedAppView_adminPanelGroupSwitcher_callout')}
            </Callout>
          </div>

          <div css={theme => ({ display: 'flex', gap: theme.space.Space2 })}>
            <Button
              css={{ flex: 1 }}
              disabled={selected.length === 0}
              label={t(
                'authenticatedAppView_groupSwitcher_clearButtonLabel ({{currentSelected}})',
                { currentSelected: selected.length },
              )}
              onClick={() => setSelected([])}
              variant="secondary"
              size="compact"
            />
            <Button
              css={{ flex: 1 }}
              label={t('common_apply_label')}
              onClick={handleSubmit}
              size="compact"
              disabled={selected.length === 0}
            />
          </div>
        </div>
      }
      query={query}
      onQueryChange={setQuery}
    >
      {loading && (
        <div css={theme => ({ padding: theme.space.Space2, width: 330 })}>
          <LoadingIndicator />
        </div>
      )}
      {!loading && filteredGroups.length === 0 && (
        <div css={theme => ({ padding: theme.space.Space2, width: 330 })}>
          {t('common_noResults_title')}
        </div>
      )}
      {currentlyAssignedGroups.length > 0 && (
        <ActionItemGroup
          label={t(
            'authenticatedAppView_adminPanelGroupSwitcherCurrentGroups_label',
          )}
        >
          {currentlyAssignedGroups.map(option => (
            <ActionItemMultiSelect
              style={{ width: 330 }}
              key={option.name}
              selected={selected.includes(option.name)}
              onClick={() => toggleGroup(option.name)}
              label={option.displayName}
            />
          ))}
        </ActionItemGroup>
      )}
      {otherGroups.length > 0 && (
        <ActionItemGroup
          label={t('authenticatedAppView_adminPanelGroupSwitcherGroups_label')}
        >
          {otherGroups.map(option => (
            <ActionItemMultiSelect
              style={{ width: 330 }}
              key={option.name}
              selected={selected.includes(option.name)}
              onClick={() => toggleGroup(option.name)}
              label={option.displayName}
            />
          ))}
        </ActionItemGroup>
      )}
    </SelectionMenu>
  )
}
